import * as React from 'react';

import type { PageProps } from 'gatsby';
import type { HeadProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import underlineBestMobile from 'stile-shared/static/underline-best-mobile.svg';
import underlineBest from 'stile-shared/static/underline-best.svg';
import underlineUniverseMobile from 'stile-shared/static/underline-universe-mobile.svg';
import underlineUniverse from 'stile-shared/static/underline-universe.svg';
import styled from 'styled-components';

import activeEarth from 'stile-shared/assets/images/curriculum/active_earth.jpg';
import andy from 'stile-shared/assets/images/curriculum/andy.jpg';
import atoms from 'stile-shared/assets/images/curriculum/atoms.png';
import auCells from 'stile-shared/assets/images/curriculum/au_cells.jpg';
import bodySystems from 'stile-shared/assets/images/curriculum/body_systems.jpg';
import filming from 'stile-shared/assets/images/curriculum/byron_filming.jpg';
import chemicalReactions from 'stile-shared/assets/images/curriculum/chemical_reactions.png';
import climateChange from 'stile-shared/assets/images/curriculum/climate_change.jpg';
import earthSystems from 'stile-shared/assets/images/curriculum/earth_systems.jpg';
import elementsAndCompounds from 'stile-shared/assets/images/curriculum/elements_and_compounds.jpg';
import energy from 'stile-shared/assets/images/curriculum/energy.jpg';
import energyConservation from 'stile-shared/assets/images/curriculum/energy_conservation.jpg';
import evolution from 'stile-shared/assets/images/curriculum/evolution.jpg';
import flashcardBackAndForth from 'stile-shared/assets/images/curriculum/flashcard_back_and_forth.png';
import flashcardFishBowl from 'stile-shared/assets/images/curriculum/flashcard_fish_bowl.png';
import flashcardFistToFive from 'stile-shared/assets/images/curriculum/flashcard_fist_to_five.png';
import foodChainsAndFoodWebs from 'stile-shared/assets/images/curriculum/food_chains.jpg';
import forces from 'stile-shared/assets/images/curriculum/forces.png';
import genetics from 'stile-shared/assets/images/curriculum/genetics.jpg';
import importanceofBio from 'stile-shared/assets/images/curriculum/importance_of_bio.jpg';
import kat from 'stile-shared/assets/images/curriculum/kat.jpg';
import kinetics from 'stile-shared/assets/images/curriculum/kinetics.png';
import mark from 'stile-shared/assets/images/curriculum/mark.jpg';
import mixtures from 'stile-shared/assets/images/curriculum/mixtures.png';
import nervousSystem from 'stile-shared/assets/images/curriculum/nervous_system.jpg';
import newton from 'stile-shared/assets/images/curriculum/newton.jpg';
import ourPlaceInSpace from 'stile-shared/assets/images/curriculum/our_place_in_space.jpg';
import physicalAndChemicalChange from 'stile-shared/assets/images/curriculum/physical_and_chemical.jpg';
import plants from 'stile-shared/assets/images/curriculum/plants.jpg';
import reactionTypes from 'stile-shared/assets/images/curriculum/reaction_types.png';
import school from 'stile-shared/assets/images/curriculum/school_filming.jpg';
import statesOfMatter from 'stile-shared/assets/images/curriculum/states_of_matter.jpg';
import stickyNotes from 'stile-shared/assets/images/curriculum/sticky_notes.jpg';
import studentResearchProject from 'stile-shared/assets/images/curriculum/student_research_project.png';
import survivalOfSpecies from 'stile-shared/assets/images/curriculum/survival_of_species.jpg';
import tShirt from 'stile-shared/assets/images/curriculum/t-shirt.jpg';
import theImmuneSystem from 'stile-shared/assets/images/curriculum/the_immune_system.png';
import periodicTable from 'stile-shared/assets/images/curriculum/the_periodic_table.png';
import theUniverse from 'stile-shared/assets/images/curriculum/the_universe.png';
import waves from 'stile-shared/assets/images/curriculum/waves.jpg';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import FlashCard from 'stile-shared/src/components/2024/common/FlashCard';
import type { FlashCardData } from 'stile-shared/src/components/2024/common/FlashCard';
import { Icon } from 'stile-shared/src/components/2024/common/Icon';
import WITBStatsSection from 'stile-shared/src/components/2024/common/Included/WITBStatsSection/WITBStatsSection';
import LessonSwiperComponent from 'stile-shared/src/components/2024/common/LessonSwiper';
import ThreeCardComponent from 'stile-shared/src/components/2024/common/ThreeCardComponent';
import { Section } from 'stile-shared/src/components/2024/layout/Section';
import VideoGallery from 'stile-shared/src/components/2024/layout/VideoGallery';

import { PageLayout } from 'templates/2024/PageLayout';
import { SEO } from 'templates/2024/SEO';

export function Head(props: HeadProps) {
  return (
    <>
      <SEO slug={props.location.pathname} />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Shantell+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
    </>
  );
}

const CurriculumLayout = styled<typeof PageLayout>(PageLayout)`
  .lesson-tile img {
    aspect-ratio: 3 / 2;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    .header-wrapper {
      background-color: var(--stile-green-2);
    }
  }

  .textBlock {
    padding: 0 0 108px 0;
  }

  .result-banner.textBlock {
    padding-bottom: 0;
  }
  .result-banner .content {
    padding-bottom: 160px !important;
  }

  /* Increase bottom margin of last lesson swiper row (desktop only) */
  .lesson-tile {
    margin-bottom: 90px !important;
  }

  /* Remove white gap between pedagogical model and stats section */
  .pedagogical-model + section {
    margin-top: 0 !important;
  }

  /* Hide 5E/SOLO diagram on mobile */
  @media (max-width: 768px) {
    .diagram-wrapper {
      display: none;
    }

    .lesson-tile {
      margin-bottom: 0 !important;
    }

    .result-banner .content {
      padding-bottom: 48px !important;
    }
    .result-banner .grid-layout h4 {
      margin-bottom: 25px !important;
    }

    .proven-practices {
      margin-bottom: 60px;

      .grid-layout h2 {
        margin-bottom: 24px !important;
      }
    }
  }

  @media (max-width: 768px) {
    .underline-20:after {
      width: 60.128px;
      height: 14.26px;
      right: 0px;
    }
    .textBlock {
      padding: 0;

      .grid-layout {
        margin-top: 0 !important;
      }
    }
  }
  .hearts {
    display: inline-block;
    left: 40px;
    @media (max-width: 768px) {
      display: flex;
      justify-content: end;
      top: 30px;
      left: 0px;
    }
  }
`;

const Banner = styled<typeof Section>(Section)`
  h4 {
    color: white;
    margin: 0 0 32px;
  }
  h1 {
    margin: 0;
  }
  .content {
    padding-top: 100px !important;
  }
  .underline-best,
  .underline-universe {
    display: inline-block;
    position: relative;

    &::after {
      content: '';
      display: block;
      position: absolute;
      background-repeat: no-repeat;
      background-position: top left;
      background-size: cover;
    }
  }

  .underline-best {
    &::after {
      width: 160px;
      height: 12px;
      bottom: -5px;
      background-image: url('${underlineBest}');
    }
  }

  .underline-universe {
    &::after {
      width: 304px;
      height: 25px;
      bottom: -8px;
      background-image: url('${underlineUniverse}');
    }
  }

  @media (max-width: 1024px) {
    .underline-best::after {
      width: 138px;
      height: 10px;
      bottom: -4px;
    }
    .underline-universe::after {
      width: 263px;
      height: 22px;
      bottom: -7px;
    }
  }

  @media (max-width: 768px) {
    &:first-child {
      .content {
        padding-top: 17px !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        margin-left: 35px;
        margin-right: 35px;
      }
    }
    .content {
      padding-top: 60px !important;
      padding-bottom: 60px !important;
    }
    h1 {
      margin-bottom: 0 !important;
    }
    h4 {
      margin-bottom: 25px;
    }
    .underline-best::after {
      width: 107px;
      height: 8px;
      bottom: -3px;
      background-image: url('${underlineBestMobile}');
    }
    .underline-universe::after {
      width: 203px;
      height: 17px;
      bottom: -5px;
      background-image: url('${underlineUniverseMobile}');
    }
  }
`;
const StickyNote = styled.div`
  background: #d0eedc;
  box-shadow: 0px 19px 19px 0px rgba(0, 0, 0, 0.06), 0px 5px 11px 0px rgba(0, 0, 0, 0.07);
  width: 100%;
  padding: 30px 30px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 40px;
  height: 100%;
  min-height: 368px;
  h5 {
    font-family: Boing;
    font-size: 32px;
    font-weight: 500;
    line-height: 125%;
    letter-spacing: -0.352px;
    flex: 1;
  }
  .h7 {
    min-height: 96px;
    flex-shrink: 0;
    max-width: 272px;
  }
  mark {
    background: rgba(0, 165, 65, 0.2);
    padding: 0 2px;
  }
  @media (max-width: 768px) {
    height: 100%;
    gap: 20px;
    min-height: 320px;
    h5 {
      font-size: 26px;
      line-height: 28px;
      flex: 1;
    }
    .h7 {
      min-height: 60px;
      flex-shrink: 0;
      font-size: 14px;
      line-height: 20px;
    }
  }
`;
const PTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: 150px;
  padding-top: 50px;
  overflow: hidden;
  .content {
    .grid-layout {
      margin-top: 25px !important;
    }
  }

  .foundation-heading {
    margin-bottom: 80px;
  }

  .sticky-notes {
    margin-bottom: 50px;
  }

  .defining-approach {
    margin-bottom: 8px;
  }

  @media (max-width: 768px) {
    padding-bottom: 72px;
    padding-top: 40px;
    .content {
      padding-top: 20px !important;

      .grid-layout {
        margin-top: 20px !important;
      }
    }

    .foundation-heading {
      margin-bottom: 40px;
    }

    .sticky-notes {
      margin-bottom: 0;
    }

    .defining-approach {
      margin-bottom: 40px;
    }

    .bringing-together {
      margin-bottom: 24px;

      .grid-layout h2 {
        margin-bottom: 24px !important;
      }
    }
  }
`;
const MobileOverflowComponent = styled.div`
  max-width: 1160px;
  justify-self: center;
  @media (max-width: 1220px) {
    padding: 0 60px;
  }
  @media (max-width: 768px) {
    padding: 0 25px;
  }
`;
const StickyNotesSection = styled(MobileOverflowComponent)`
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, auto);
  gap: 80px 28px;
  display: grid;
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 12px;
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
    position: relative;
    padding-bottom: 50px;

    & > div {
      flex: 0 0 300px;
      min-width: 300px;
      scroll-snap-align: start;
    }
    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
  }
`;
const PedagogicalModelSection = styled.div`
  background: #5e60ab;
  padding: 150px 0;
  display: flex;
  justify-content: center;

  .inner {
    display: flex;
    align-items: center;
    gap: 60px;
    max-width: 1160px;
    width: 100%;
    padding: 0 60px;
  }

  .left {
    flex: 1;
    max-width: 500px;
    color: var(--white);

    h2 {
      margin: 0 0 24px;
      color: var(--white);
    }

    p {
      margin: 0 0 32px;
      color: rgba(255, 255, 255, 0.85);
      line-height: 1.6;
    }
  }

  .right {
    flex: 1;
    max-width: 520px;

    img {
      width: 100%;
      border-radius: 20px;
      box-shadow: 0px 43px 26px 0px rgba(0, 0, 0, 0.1), 0px 19px 19px 0px rgba(0, 0, 0, 0.12),
        0px 5px 11px 0px rgba(0, 0, 0, 0.14);
    }

    .gatsby-image-wrapper {
      border-radius: 20px;
      box-shadow: 0px 43px 26px 0px rgba(0, 0, 0, 0.1), 0px 19px 19px 0px rgba(0, 0, 0, 0.12),
        0px 5px 11px 0px rgba(0, 0, 0, 0.14);
    }
  }

  @media (max-width: 768px) {
    padding: 60px 25px;

    .inner {
      flex-direction: column;
      gap: 32px;
      padding: 0;
    }

    .left {
      max-width: 100%;
    }

    .right {
      max-width: 100%;
    }
  }
`;

const PTAAcademySection = styled<typeof Section>(Section)`
  margin: 0;
  padding: 0 0 0 0;
  grid-column: span 12;
  position: relative;
  &:last-child {
    padding-bottom: 150px;
    @media (max-width: 768px) {
      padding-bottom: 30px;
    }
  }
  p {
    grid-column: 4 / span 6;
    text-align: center;
  }
  .diagram {
    grid-column: 1 / span 12;
    margin-top: 80px;
  }
  .red {
    color: #ef4e4a;
  }
  .green {
    color: var(--stile-green-3);
  }
  .red,
  .green,
  .bold {
    font-weight: 600;
  }
  @media (max-width: 768px) {
    h2 {
      margin-bottom: 0 !important;
    }
    padding: 0;
    .content {
      padding: 0;
    }
    p {
      grid-column: 1 / span 12;
    }
  }
  .subheading {
    align-self: center;
  }
  .subheader {
    grid-column: 3 / span 8;
    text-align: center;
    margin: 0;

    h4 {
      margin-bottom: 32px;
      @media (max-width: 768px) {
        margin-bottom: 20px;
      }
    }

    p {
      margin: 0;
    }
    @media (max-width: 768px) {
      grid-column: 1 / span 12;

      p {
        &:first-child {
          margin-top: 0;
        }
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .content {
    .grid-layout {
      grid-column: span 12;
      align-items: center;
    }

    @media (max-width: 768px) {
      padding-top: 20px !important;
      .grid-layout {
        margin-bottom: 0 !important;
      }
      .report-button-wrapper {
        grid-column: 1 / span 12 !important;
        justify-content: center !important;
        a {
          white-space: normal;
          text-align: center;
        }
      }
    }
  }
`;

const ScopeAndSequence = styled(PTAAcademySection)`
  padding-bottom: 80px;
  @media (max-width: 768px) {
    padding: 0 0 40px;
    margin-bottom: 0;

    .grid-layout h2 {
      margin-bottom: 24px !important;
    }
  }
`;

const EngagementStrategiesSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 150px 25px 150px;
  background-color: var(--stile-grey-1);

  .engagement-heading {
    text-align: center;
    max-width: 773px;
    margin: 0 0 32px;
  }

  .engagement-subtext {
    text-align: center;
    max-width: 500px;
    margin: 0 0 32px;
    color: var(--stile-grey-8);
  }

  .flashcard-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 28px;
    justify-content: center;
    margin-top: 80px;
    max-width: 773px;
    width: 100%;
  }

  .flashcard-with-badge {
    position: relative;
  }

  .image-card {
    border-radius: 20px;
    overflow: hidden;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .hands-on-badge {
    position: absolute;
    top: -129px;
    right: -35px;
    width: 152px;
    height: 152px;
    min-width: 152px;
    min-height: 152px;
    z-index: 1;

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }

    .badge-text {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: var(--white);
      font-family: Boing;
      font-size: 20px;
      font-weight: 500;
      line-height: 1.2;
      text-align: center;
      white-space: nowrap;
    }

    &.mobile-only {
      display: none;
    }
  }

  .tap-to-view {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 60px 25px 60px;

    .flashcard-grid {
      grid-template-columns: 1fr;
      gap: 24px;
      width: 100%;
      margin-top: 20px;
      justify-items: center;
    }

    .image-card-wrapper {
      display: none;
    }

    .tap-to-view {
      display: block;
      font-size: 14px;
      line-height: 18px;
      color: var(--stile-grey-5);
      text-align: center;
      margin: 0;
      padding-top: 42px;
    }

    .hands-on-badge {
      top: -75px;
      right: -10px;
      width: 110px;
      height: 110px;
      min-width: 110px;
      min-height: 110px;

      .badge-text {
        font-size: 13px;
      }

      &.desktop-only {
        display: none;
      }

      &.mobile-only {
        display: block;
      }
    }
  }
`;

const StyledVideoGallery = styled(VideoGallery)`
  margin-top: 80px;
  @media (max-width: 768px) {
    padding: 0 !important;
    margin-top: 16px;
  }
`;
function CurriculumPage(props: PageProps) {
  const year7Lessons = [
    {
      coverImage: mixtures,
      name: 'Mixtures',
      description: 'Can we 3D print new bones to replace broken ones?',
    },
    {
      coverImage: statesOfMatter,
      name: 'States of Matter',
      description: 'Why is liquid water so important for humans to live on Mars?',
    },
    {
      coverImage: foodChainsAndFoodWebs,
      name: 'Food Chains and Food Webs',
      description: 'Why do cats have slit-shaped pupils?',
    },
    {
      coverImage: importanceofBio,
      name: 'Classification and Biodiversity',
      description: 'Do we need to save the bees?',
    },
    {
      coverImage: forces,
      name: 'Forces',
      description: 'How have people used forces for thousands of years?',
    },
    {
      coverImage: studentResearchProject,
      name: 'Student Research Project',
      description: 'Teaching students how to complete scientific research',
    },
    {
      coverImage: ourPlaceInSpace,
      name: 'Our Place in Space',
      description: 'Can we travel to the Sun?',
    },
  ];
  const year8Lessons = [
    {
      coverImage: auCells,
      name: 'Cells',
      description: 'Would you eat lab-grown \u201cmeat\u201d?',
    },
    {
      coverImage: bodySystems,
      name: 'Body Systems',
      description: 'What does it take to be a cold-blooded killer?',
    },
    {
      coverImage: nervousSystem,
      name: 'The Nervous System',
      description: 'How can your gut influence your mood?',
    },
    {
      coverImage: energy,
      name: 'Energy',
      description: 'How can we learn from nature to improve energy technology?',
    },
    {
      coverImage: physicalAndChemicalChange,
      name: 'Physical and Chemical Change',
      description: 'What does chemistry have to do with chocolate making?',
    },
    {
      coverImage: elementsAndCompounds,
      name: 'Elements and Compounds',
      description: 'If we need to mine lithium, how do we minimise the impact?',
    },
    {
      coverImage: activeEarth,
      name: 'Active Earth',
      description: 'How do we build future-ready cities?',
    },
  ];
  const year9Lessons = [
    {
      coverImage: earthSystems,
      name: 'Earth Systems',
      description: 'How does our planet recycle?',
    },
    {
      coverImage: theImmuneSystem,
      name: 'The Immune System',
      description: 'How can we protect communities from disease?',
    },
    {
      coverImage: plants,
      name: 'Plants',
      description: 'How can we feed an off-world colony?',
    },
    {
      coverImage: survivalOfSpecies,
      name: 'The Survival of Species',
      description: 'A dating guide for animals',
    },
    {
      coverImage: atoms,
      name: 'Atoms',
      description: 'How can the building blocks of atoms help us see further?',
    },
    {
      coverImage: chemicalReactions,
      name: 'Chemical Reactions',
      description: 'What happens when sodium explodes in water?',
    },
    {
      coverImage: waves,
      name: 'Waves',
      description: 'How does someone on the other side of the world hear you?',
    },
    {
      coverImage: energyConservation,
      name: 'Energy Conservation \u2013 Hydropower',
      description: 'Should we rely on hydropower for our energy needs?',
    },
  ];
  const year10Lessons = [
    {
      coverImage: genetics,
      name: 'Genetics',
      description: 'Can genes increase the risk of cancer?',
    },
    {
      coverImage: evolution,
      name: 'Evolution',
      description: 'Are we responsible for the rise of superbugs?',
    },
    {
      coverImage: kinetics,
      name: 'Kinetics',
      description: 'Are self-driving cars the way of the future?',
    },
    {
      coverImage: newton,
      name: "Newton's Laws of Motion",
      description: "How can we use Newton's Laws in car crash investigations?",
    },
    {
      coverImage: periodicTable,
      name: 'The Periodic Table',
      description: 'How do exploding stars create heavy metals?',
    },
    {
      coverImage: reactionTypes,
      name: 'Reaction Types',
      description: 'Are self-healing space suits science fiction or just science?',
    },
    {
      coverImage: climateChange,
      name: 'Climate Change',
      description: 'Climate change \u2013 how should we respond?',
    },
    {
      coverImage: theUniverse,
      name: 'The Universe',
      description: 'How do gravitational waves give us a new way of understanding the universe?',
    },
  ];
  const exploreCards = [
    {
      title: 'Stile X Workbooks',
      description:
        'Designed for hands-on learning and perfectly matched to the digital curriculum.',
      url: '/what-is-stile/stile-x/',
    },
    {
      title: 'Assessments',
      description: 'Teach with a real-time pulse on student understanding.',
      url: '/what-is-stile/assessments/',
    },
    {
      title: 'Professional Learning',
      description:
        'Master Stile at your own pace with evidence-based pedagogies and personalised support.',
      url: '/what-is-stile/professional-learning/',
    },
  ];

  const orange = '#FF5C3B';
  const indigo = '#5E60AB';
  const lava = '#EF4E4A';

  const flashCards: FlashCardData[] = [
    {
      title: 'Fish Bowl',
      color: orange,
      frontDescription: 'A small group discusses a topic while the rest of the class observes.',
      frontImage: <img src={flashcardFishBowl} alt="Fish Bowl strategy illustration" />,
      backContent: (
        <>
          <p>
            <span style={{ color: orange, fontWeight: 600 }}>Fish Bowl</span> allows for focused
            conversation and reflective observation, encourages active listening and note-taking,
            and helps students learn from their peers' discussion techniques.
          </p>
          <div className="section">
            <p className="section-label">WHEN</p>
            <p className="section-body">
              <span style={{ color: orange, fontWeight: 600 }}>Discussion: </span>
              For in-depth discussions and debates.
              <br />
              <span style={{ color: orange, fontWeight: 600 }}>Modelling: </span>
              To model discussion techniques and behaviours.
              <br />
              <span style={{ color: orange, fontWeight: 600 }}>Reflection: </span>
              When reflecting on complex topics.
            </p>
          </div>
          <div className="section">
            <p className="section-label">HOW</p>
            <p className="section-body">
              <span style={{ color: orange, fontWeight: 600 }}>Arrange</span> chairs in an inner and
              outer circle. Ask the inner group to{' '}
              <span style={{ color: orange, fontWeight: 600 }}>discuss</span> the topic while the
              outer group listens and takes notes.{' '}
              <span style={{ color: orange, fontWeight: 600 }}>Switch</span> roles so everyone has a
              chance to participate in the discussion. Then{' '}
              <span style={{ color: orange, fontWeight: 600 }}>debrief </span>
              and discuss key points and observations with the whole class.
            </p>
          </div>
        </>
      ),
    },
    {
      title: 'Back and Forth',
      color: indigo,
      frontDescription: 'Students explain a concept to a partner, who then writes it down.',
      frontImage: <img src={flashcardBackAndForth} alt="Back and Forth strategy illustration" />,
      backContent: (
        <>
          <p>
            <span style={{ color: indigo, fontWeight: 600 }}>Back and Forth</span> allows students
            to share their understanding and check each other's explanations for accuracy. This
            approach helps students correct misunderstandings and solidify their grasp of a topic.
          </p>
          <div className="section">
            <p className="section-label">WHEN</p>
            <p className="section-body">
              <span style={{ color: indigo, fontWeight: 600 }}>Entry task: </span>
              Review previous content and activate prior knowledge.
              <br />
              <span style={{ color: indigo, fontWeight: 600 }}>New vocabulary: </span>
              Reinforce understanding and correct usage of new vocabulary.
              <br />
              <span style={{ color: indigo, fontWeight: 600 }}>Review: </span>
              Recall and solidify understanding of previously covered content.
            </p>
          </div>
          <div className="section">
            <p className="section-label">HOW</p>
            <p className="section-body">
              Students split into <span style={{ color: indigo, fontWeight: 600 }}>pairs</span>.
              Partner A <span style={{ color: indigo, fontWeight: 600 }}>explains</span> a problem,
              process, concept, etc. to Partner B, who{' '}
              <span style={{ color: indigo, fontWeight: 600 }}>writes</span> down the explanation.
              Partner A <span style={{ color: indigo, fontWeight: 600 }}>checks</span> the
              explanation for accuracy until both agree. Partners{' '}
              <span style={{ color: indigo, fontWeight: 600 }}>reverse</span> roles and repeat.
            </p>
          </div>
        </>
      ),
    },
    {
      title: 'Fist to Five',
      color: lava,
      frontDescription:
        'Students show their understanding of a concept using a scale from 0 (fist) to 5 (five fingers).',
      frontImage: <img src={flashcardFistToFive} alt="Fist to Five strategy illustration" />,
      backContent: (
        <>
          <p>
            <span style={{ color: lava, fontWeight: 600 }}>Fist to Five </span>
            allows for a quick gauge of student understanding. It encourages active participation,
            and promotes inclusivity by involving all students, creating a dynamic and responsive
            learning environment.
          </p>
          <div className="section">
            <p className="section-label">WHEN</p>
            <p className="section-body">
              <span style={{ color: lava, fontWeight: 600 }}>Ice breaker: </span>
              At the start of a lesson to assess initial understanding.
              <br />
              <span style={{ color: lava, fontWeight: 600 }}>To close: </span>
              At the end of a lesson to confirm overall understanding.
              <br />
              <span style={{ color: lava, fontWeight: 600 }}>Quick feedback: </span>
              Whenever immediate feedback is needed on any activity or topic.
            </p>
          </div>
          <div className="section">
            <p className="section-label">HOW</p>
            <p className="section-body">
              <span style={{ color: lava, fontWeight: 600 }}>Teach</span> the levels of
              understanding from 0/fist (no understanding) to 5 fingers (complete understanding).
              Ask students to show their understanding level at key points.{' '}
              <span style={{ color: lava, fontWeight: 600 }}>Prompt</span> again if needed, and
              adjust instruction as necessary.
            </p>
          </div>
        </>
      ),
    },
  ];

  const statsData = [
    { value: '62', label: 'topics' },
    { value: '1,144', label: 'lessons' },
    { value: '73', label: 'labs' },
    { value: '215', label: 'assessments' },
    { value: '1,317', label: 'videos' },
    { value: '10,918', label: 'questions' },
    { value: '58', label: 'career profiles' },
    { value: '52', label: 'simulations' },
  ];
  return (
    <CurriculumLayout {...props}>
      <Banner
        className="textBlock"
        backgroundColor="--stile-green-2"
        headingContent={
          <>
            <h4>Stile's Secondary School Curriculum</h4>
            <h1 className="--jumbo">
              We aspire to make the <span className="underline-best">best</span> science lessons in
              the known <span className="underline-universe">universe</span>. <br />
              <br />
              Here's how we do it.
            </h1>
          </>
        }
      ></Banner>
      <PTAWrapper>
        <PTAAcademySection
          className="foundation-heading"
          backgroundColor="--white"
          headingColor="--stile-grey-8"
          headingContent="What do students need to know and be capable of doing?"
          subHeadingContent="THE FOUNDATION"
          subHeadingColor="--stile-green-3"
          textColor="--stile-grey-8"
        />
        <StickyNotesSection className="sticky-notes">
          <StickyNote>
            <h5>
              What do students need to know in order to be <mark>scientifically literate?</mark>
            </h5>
            <h6 className="h7">Eg. How does antibiotic resistance work and why is it an issue?</h6>
          </StickyNote>
          <StickyNote>
            <h5>
              How is science <mark>relevant to careers</mark> other than being a scientist?
            </h5>
            <h6 className="h7">
              E.g. Chocolatiers use chemistry to create new textures and flavours.
            </h6>
          </StickyNote>
          <StickyNote>
            <h5>
              How do we teach students the <mark>language</mark> of science?
            </h5>
            <h6 className="h7">
              E.g. Mathematical thinking: creating models, interpreting, analysing and visualising
              data.
            </h6>
          </StickyNote>
          <StickyNote>
            <h5>
              How science intersects with <mark>society</mark>, and how it likely will{' '}
              <mark>tomorrow?</mark>
            </h5>
            <h6 className="h7">
              E.g. Understanding the economic, environmental and social factors slowing the
              deployment of more renewable energy.
            </h6>
          </StickyNote>
          <StickyNote>
            <h5>
              What <mark>skills</mark> and <mark>dispositions</mark> are needed to be critical
              thinkers?
            </h5>
            <h6 className="h7">
              E.g. Evaluating claims using evidence, understanding the statistical significance of
              that evidence.
            </h6>
          </StickyNote>
          <StickyNote>
            <h5>
              We then fill in the rest to ensure we <mark>completely cover</mark> the standards
            </h5>
            <h6 className="h7">
              E.g. The Australian Curriculum, as well as state and territory curricula.
            </h6>
          </StickyNote>
        </StickyNotesSection>

        <PTAAcademySection
          className="defining-approach"
          backgroundColor="transparent"
          headingColor="--stile-grey-8"
          headingContent={
            <h2>Combine proven best-practices with what actually works in real classrooms</h2>
          }
          subHeadingContent="DEFINING OUR APPROACH"
          subHeadingColor="--stile-green-3"
          textColor="--stile-grey-8"
        ></PTAAcademySection>
        <ThreeCardComponent
          cards={[
            {
              heading: 'Aligning each state\u2019s standards with local context',
              copy: 'We bring Australia\u2019s real-world context into every unit, crafting lessons that feel locally relevant and meaningful, while fulfilling the requirements of both national and state curricula.',
              image: (
                <StaticImage
                  src="../../../../stile-shared/assets/images/curriculum/au_standards.png"
                  alt="Australian lessons featuring local context, aligned to the Australian Curriculum and state curricula"
                  quality={100}
                />
              ),
            },
            {
              heading: 'Evidence-based pedagogies',
              copy: 'We then weave in evidence-based pedagogies, using the ones that best fit the context of the lesson and unit.',
              image: (
                <StaticImage
                  src="../../../../stile-shared/assets/images/curriculum/au_pedagogies.png"
                  alt="Evidence-based pedagogies: 5E Instructional Model, SOLO Taxonomy, Cognitive Load Theory, Explicit Instruction, Differentiation, and more"
                  quality={100}
                />
              ),
            },
            {
              heading: 'High-impact teaching strategies',
              copy: 'Finally we make the use of high-impact teaching strategies the path of least resistance.',
              image: (
                <StaticImage
                  src="../../../../stile-shared/assets/images/curriculum/strategies.png"
                  alt="Strategies"
                  quality={100}
                />
              ),
            },
          ]}
        />

        {/* Analyse */}
        <PTAAcademySection
          className="bringing-together"
          backgroundColor="transparent"
          headingColor="--stile-grey-8"
          headingContent="Built by our in-house experts"
          subHeadingContent="BRINGING IT TOGETHER"
          subHeadingColor="--stile-green-3"
          textColor="--stile-grey-8"
        >
          <p>
            Our talented team of educators, scientists, artists and engineers create Stile's
            curriculum in collaboration with an entourage of out-of-house practising teachers,
            pedagogy academics and subject-matter experts.
          </p>
        </PTAAcademySection>
        <StyledVideoGallery
          className="teachers"
          videoAspectRatio="1"
          items={[
            { type: 'img', url: kat },
            { type: 'img', url: stickyNotes },
            { type: 'img', url: tShirt },
            { type: 'img', url: mark },
            { type: 'img', url: filming },
            { type: 'img', url: andy },
            { type: 'img', url: school },
          ]}
        />
      </PTAWrapper>
      <Banner
        className="textBlock result-banner"
        backgroundColor="--stile-green-2"
        headingContent={
          <>
            <h4>The result?</h4>
            <h1>
              A complete science program that teachers love to teach — and students love to learn.
            </h1>
          </>
        }
      ></Banner>
      <ScopeAndSequence
        backgroundColor="--stile-grey-1"
        headingContent="The secondary school scope and sequence"
      >
        <p>
          A sequence of learning packed with phenomena that students <em>actually</em> want to
          discover for themselves.
        </p>
      </ScopeAndSequence>
      <LessonSwiperComponent heading="Year 7" lessons={year7Lessons} />
      <LessonSwiperComponent heading="Year 8" lessons={year8Lessons} />
      <LessonSwiperComponent heading="Year 9" lessons={year9Lessons} />
      <LessonSwiperComponent heading="Year 10" lessons={year10Lessons} />
      <PTAAcademySection
        className="proven-practices"
        headingContent="Units built on proven practices"
      >
        <p>
          Stile's units are grounded in the <span className="green">5 E instructional model</span>{' '}
          and scaffold students to "extended abstract" thinking as defined in{' '}
          <span className="red">SOLO taxonomy</span>, where they can generalise their understanding
          and apply it in a new domain.
        </p>
        <p>
          Stile's instructional framework was reviewed by Alan J. Reid (PhD) and Steve Ross (PhD)
          from{' '}
          <span className="bold">
            {' '}
            Johns Hopkins University's Centre for Research and Reform in Education
          </span>
          . They determined that Stile's curriculum is grounded in rigorous research and had a
          meticulously designed logic model.
          <br />
          <br />
        </p>
        <div
          className="report-button-wrapper"
          style={{
            gridColumn: '4 / span 6',
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap' as const,
          }}
        >
          <Button
            href="/resources/johns-hopkins-report/"
            newTab={true}
            label={
              <div>
                <Icon iconId="document" iconSize="fontSize" style={{ marginRight: 10 }} />
                Download the full Johns Hopkins report
              </div>
            }
          />
        </div>
      </PTAAcademySection>
      <MobileOverflowComponent className="diagram-wrapper">
        <StaticImage
          src="../../../../stile-shared/assets/images/curriculum/5e_solo.png"
          alt="5E model and SOLO taxonomy"
          className="diagram"
          style={{ marginTop: '80px', marginBottom: '150px' }}
        />
      </MobileOverflowComponent>
      <EngagementStrategiesSection>
        <h2 className="engagement-heading">
          Engagement strategies that foster collaboration and deeper thinking
        </h2>
        <p className="engagement-subtext">
          Stile lessons include proven techniques designed to actively engage every student in your
          science classroom.
        </p>
        <Button
          href="https://blog.stileeducation.com/classroom-engagement-strategies-widget/"
          newTab
          label="Learn more"
          style="primary"
        />
        <p className="tap-to-view">Tap to view</p>
        <div className="flashcard-grid">
          <div className="flashcard-with-badge">
            <div className="hands-on-badge mobile-only">
              <svg
                viewBox="5.5 5.5 155 155"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M76.942 10.1528C79.6765 5.56757 86.3173 5.56756 89.0518 10.1528C91.3066 13.9337 96.4366 14.7462 99.7494 11.8471C103.767 8.33131 110.083 10.3835 111.267 15.5893C112.243 19.8818 116.87 22.2398 120.917 20.5064C125.824 18.4042 131.197 22.3075 130.714 27.6243C130.316 32.0084 133.988 35.6811 138.373 35.283C143.689 34.8001 147.593 40.1726 145.491 45.08C143.757 49.1264 146.115 53.7543 150.408 54.7304C155.613 55.9141 157.666 62.2299 154.15 66.2474C151.251 69.5602 152.063 74.6902 155.844 76.945C160.429 79.6795 160.429 86.3203 155.844 89.0548C152.063 91.3096 151.251 96.4396 154.15 99.7524C157.666 103.77 155.613 110.086 150.408 111.27C146.115 112.246 143.757 116.873 145.491 120.92C147.593 125.827 143.689 131.2 138.373 130.717C133.988 130.319 130.316 133.991 130.714 138.376C131.197 143.692 125.824 147.596 120.917 145.494C116.87 143.76 112.243 146.118 111.267 150.411C110.083 155.616 103.767 157.669 99.7494 154.153C96.4366 151.254 91.3066 152.066 89.0518 155.847C86.3173 160.432 79.6765 160.432 76.942 155.847C74.6872 152.066 69.5572 151.254 66.2444 154.153C62.2269 157.669 55.9111 155.616 54.7274 150.411C53.7513 146.118 49.1234 143.76 45.077 145.494C40.1696 147.596 34.7971 143.692 35.28 138.376C35.6781 133.991 32.0054 130.319 27.6213 130.717C22.3045 131.2 18.4012 125.827 20.5034 120.92C22.2368 116.873 19.8788 112.246 15.5863 111.27C10.3805 110.086 8.32838 103.77 11.8441 99.7524C14.7432 96.4396 13.9307 91.3096 10.1498 89.0548C5.56463 86.3203 5.56464 79.6795 10.1498 76.945C13.9307 74.6902 14.7432 69.5602 11.8441 66.2474C8.32838 62.2299 10.3805 55.9141 15.5863 54.7304C19.8788 53.7543 22.2368 49.1264 20.5034 45.08C18.4012 40.1726 22.3045 34.8001 27.6213 35.283C32.0054 35.6811 35.6781 32.0084 35.28 27.6243C34.7971 22.3075 40.1696 18.4042 45.077 20.5064C49.1234 22.2398 53.7513 19.8818 54.7274 15.5893C55.9111 10.3835 62.2269 8.33131 66.2444 11.8471C69.5572 14.7462 74.6872 13.9337 76.942 10.1528Z"
                  fill="#1094C0"
                />
              </svg>
              <span className="badge-text">
                Hands-on.
                <br />
                Off-device.
              </span>
            </div>
            <FlashCard card={flashCards[0]} hintFlip />
          </div>
          <div className="flashcard-with-badge image-card-wrapper">
            <div className="hands-on-badge">
              <svg
                viewBox="5.5 5.5 155 155"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M76.942 10.1528C79.6765 5.56757 86.3173 5.56756 89.0518 10.1528C91.3066 13.9337 96.4366 14.7462 99.7494 11.8471C103.767 8.33131 110.083 10.3835 111.267 15.5893C112.243 19.8818 116.87 22.2398 120.917 20.5064C125.824 18.4042 131.197 22.3075 130.714 27.6243C130.316 32.0084 133.988 35.6811 138.373 35.283C143.689 34.8001 147.593 40.1726 145.491 45.08C143.757 49.1264 146.115 53.7543 150.408 54.7304C155.613 55.9141 157.666 62.2299 154.15 66.2474C151.251 69.5602 152.063 74.6902 155.844 76.945C160.429 79.6795 160.429 86.3203 155.844 89.0548C152.063 91.3096 151.251 96.4396 154.15 99.7524C157.666 103.77 155.613 110.086 150.408 111.27C146.115 112.246 143.757 116.873 145.491 120.92C147.593 125.827 143.689 131.2 138.373 130.717C133.988 130.319 130.316 133.991 130.714 138.376C131.197 143.692 125.824 147.596 120.917 145.494C116.87 143.76 112.243 146.118 111.267 150.411C110.083 155.616 103.767 157.669 99.7494 154.153C96.4366 151.254 91.3066 152.066 89.0518 155.847C86.3173 160.432 79.6765 160.432 76.942 155.847C74.6872 152.066 69.5572 151.254 66.2444 154.153C62.2269 157.669 55.9111 155.616 54.7274 150.411C53.7513 146.118 49.1234 143.76 45.077 145.494C40.1696 147.596 34.7971 143.692 35.28 138.376C35.6781 133.991 32.0054 130.319 27.6213 130.717C22.3045 131.2 18.4012 125.827 20.5034 120.92C22.2368 116.873 19.8788 112.246 15.5863 111.27C10.3805 110.086 8.32838 103.77 11.8441 99.7524C14.7432 96.4396 13.9307 91.3096 10.1498 89.0548C5.56463 86.3203 5.56464 79.6795 10.1498 76.945C13.9307 74.6902 14.7432 69.5602 11.8441 66.2474C8.32838 62.2299 10.3805 55.9141 15.5863 54.7304C19.8788 53.7543 22.2368 49.1264 20.5034 45.08C18.4012 40.1726 22.3045 34.8001 27.6213 35.283C32.0054 35.6811 35.6781 32.0084 35.28 27.6243C34.7971 22.3075 40.1696 18.4042 45.077 20.5064C49.1234 22.2398 53.7513 19.8818 54.7274 15.5893C55.9111 10.3835 62.2269 8.33131 66.2444 11.8471C69.5572 14.7462 74.6872 13.9337 76.942 10.1528Z"
                  fill="#1094C0"
                />
              </svg>
              <span className="badge-text">
                Hands-on.
                <br />
                Off-device.
              </span>
            </div>
            <div className="image-card">
              <StaticImage
                src="../../../../stile-shared/assets/images/curriculum/engagement_strategies.jpg"
                alt="Students collaborating in a hands-on, off-device science activity"
              />
            </div>
          </div>
          <FlashCard card={flashCards[1]} />
          <FlashCard card={flashCards[2]} />
        </div>
      </EngagementStrategiesSection>
      <PedagogicalModelSection className="pedagogical-model">
        <div className="inner">
          <div className="left">
            <h2>See Stile’s pedagogical model in situ</h2>
            <p>
              Download the Teaching Plan for Stile’s Plants unit to see what our unit structure
              looks like in practice. The Teaching Plan outlines the phenomena, storyline and career
              profile, as well as the list of lessons, labs and assessments.
            </p>
            <Button
              href="/resources/plants-unit-teaching-plan/"
              newTab={true}
              label={
                <div>
                  <Icon iconId="document" iconSize="fontSize" style={{ marginRight: 10 }} />
                  Download the Plants unit Teaching Plan
                </div>
              }
            />
          </div>
          <div className="right">
            <StaticImage
              src="../../../../stile-shared/assets/images/curriculum/plants_unit.jpg"
              alt="The Plants unit Teaching Plan"
            />
          </div>
        </div>
      </PedagogicalModelSection>
      <WITBStatsSection title="Your core curriculum, ready to go" stats={statsData} />
      <ExploreCurriculum
        title="Explore our core science curriculum"
        exploreCardsData={exploreCards}
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/responsive-support/book-with-grad-cap.svg"
            alt="A book with a graduation cap"
          />
        }
      />
    </CurriculumLayout>
  );
}

export default CurriculumPage;
